/**
 * iter63z''''''' — Tenant branding theming bootstrapper.
 *
 * Reads `/api/tenants/me` on app boot, applies CSS variables
 * `--brand-primary` + `--brand-secondary` to the document root, and
 * updates the browser tab title to the tenant's app_name. Components
 * that want to follow tenant branding use `var(--brand-primary)` /
 * `var(--brand-secondary)` in their styles. The pre-existing
 * volt/cyber Tailwind classes still work for everything else — this
 * is purely additive for tenant-aware surfaces.
 *
 * Listens for `tcr:branding-updated` window events (fired by the
 * Branding tab on save) so changes apply instantly without a reload.
 *
 * Failure mode: if the API call fails (e.g. backend booting), keeps
 * the platform defaults — no flash of unstyled content.
 */
import { useEffect } from "react";
import { api } from "../lib/api";

const PLATFORM_DEFAULTS = {
    primary_color: "#CCFF00",
    secondary_color: "#00E5FF",
    app_name: "Tiny Crew Rivalries",
};

function applyBranding(b) {
    const primary = b?.primary_color || PLATFORM_DEFAULTS.primary_color;
    const secondary = b?.secondary_color || PLATFORM_DEFAULTS.secondary_color;
    const name = b?.app_name || PLATFORM_DEFAULTS.app_name;
    const root = document.documentElement;
    root.style.setProperty("--brand-primary", primary);
    root.style.setProperty("--brand-secondary", secondary);
    if (name && document.title !== name) {
        document.title = name;
    }
}

export default function TenantBrandingProvider() {
    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const r = await api.getTenantMe();
                if (!mounted) return;
                applyBranding(r.branding);
                // Stash on window for other components that want to read
                // app_name / logo_url without re-hitting the API.
                window.__tcr_tenant = r;
                window.dispatchEvent(
                    new CustomEvent("tcr:branding-loaded", { detail: r }),
                );
            } catch (e) {
                // Backend not ready — fall back to platform defaults.
                applyBranding(null);
            }
        })();
        const onUpdate = (ev) => {
            applyBranding(ev.detail);
            if (window.__tcr_tenant) {
                window.__tcr_tenant.branding = ev.detail;
            }
        };
        window.addEventListener("tcr:branding-updated", onUpdate);
        return () => {
            mounted = false;
            window.removeEventListener("tcr:branding-updated", onUpdate);
        };
    }, []);
    return null;
}
